import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Container,
  Row,
  Col,
  Input,
  Button,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import AdminHeader from "components/Headers/AdminHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import { getCertificateIssuanceReport } from "action/report";
import { certificateIssuanceReport } from "constants/report";

function CertificateIssuanceReport() {
  const initialFilters = {
    course: "",
    trainee: "",
    batch: "",
    dateRange: [null, null],
  };

  const dispatch = useDispatch();

  const certificateIssuanceReportData = useSelector(
    (state) => state.report.certificateIssuanceReport
  );

  const [filters, setFilters] = useState(initialFilters);

  const [filterState, setFilterState] = useState({
    filterParams: [],
  });

  const [pageNum, setPageNum] = useState(1);

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  useEffect(() => {
    getCertificateIssuanceReport(
      dispatch,
      filterState.filterParams,
      pageNum
    );

    return () => {
      dispatch({
        type: certificateIssuanceReport,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (certificateIssuanceReportData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          certificateIssuanceReportData?.data?.certificateIssuanceReport
            ?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (
      certificateIssuanceReportData?.code ||
      certificateIssuanceReportData?.message
    ) {
      setListState({
        isLoading: false,
        error:
          certificateIssuanceReportData?.message ||
          "Failed to load certificate issuance report.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [certificateIssuanceReportData]);

  const formatDate = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.course?.trim()) {
      filterParams.push({
        field: "CourseName",
        condition: "like",
        filterValues: [filters.course.trim()],
      });
    }

    if (filters.trainee?.trim()) {
      filterParams.push({
        field: "TraineeName",
        condition: "like",
        filterValues: [filters.trainee.trim()],
      });
    }

    if (filters.batch?.trim()) {
      filterParams.push({
        field: "BatchName",
        condition: "like",
        filterValues: [filters.batch.trim()],
      });
    }

    const [startDate, endDate] = filters.dateRange;

    if (startDate && endDate) {
      filterParams.push({
        field: "IssueDate",
        condition: "btw",
        filterValues: [formatDate(startDate), formatDate(endDate)],
      });
    } else if (startDate) {
      filterParams.push({
        field: "IssueDate",
        condition: "eq",
        filterValues: [formatDate(startDate)],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((previous) => ({
        ...previous,
        isLoading: true,
        error: "",
        showLoaderOnClick: true,
      }));

      setFilterState({
        filterParams: newFilterParams,
      });

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setListState((previous) => ({
      ...previous,
      isLoading: true,
      error: "",
      showLoaderOnClick: false,
    }));

    setFilters(initialFilters);

    setFilterState({
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 80,
      centerAlign: true,
    },
    {
      Header: "Certificate Number",
      accessor: "certificateNumber",
      minWidth: 170,
      maxWidth: 210,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Trainee",
      accessor: "trainee",
      minWidth: 160,
      maxWidth: 210,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Course",
      accessor: "course",
      minWidth: 220,
      maxWidth: 280,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Batch",
      accessor: "batch",
      minWidth: 180,
      maxWidth: 240,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Issue Date",
      accessor: "issueDate",
      minWidth: 180,
      maxWidth: 210,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Validity",
      accessor: "validity",
      minWidth: 120,
      maxWidth: 140,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Expiry Date",
      accessor: "expiryDate",
      minWidth: 150,
      maxWidth: 170,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
  ];

  const tableData = (
    certificateIssuanceReportData?.data?.certificateIssuanceReport || []
  ).map((item, index) => ({
    ...item,
    id: item?.id ?? index,
    srNo:
      (pageNum - 1) *
        (certificateIssuanceReportData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
    certificateNumber: item?.certificateNo || "-",
    trainee: item?.traineeName || "-",
    course: item?.courseName || "-",
    batch: item?.batchName || "-",
    issueDate: item?.issueDate || "-",
    validity: item?.validity || "-",
    expiryDate: item?.expiryDate || "-",
  }));

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Certificate Issuance Report
                </CardTitle>
              </CardHeader>

              <CardBody>
                {/* Filters */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      name="trainee"
                      placeholder="Search by trainee"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.trainee}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />
                    
                    <Input
                      type="text"
                      name="course"
                      placeholder="Search by course"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.course}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      name="batch"
                      placeholder="Search by batch"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.batch}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <DatePicker
                      selectsRange
                      startDate={filters.dateRange[0]}
                      endDate={filters.dateRange[1]}
                      onChange={(update) =>
                        setFilters((previous) => ({
                          ...previous,
                          dateRange: update,
                        }))
                      }
                      isClearable
                      placeholderText="Select issue date or range"
                      className="form-control mr-2 mb-2 filter-input-large"
                      dateFormat="dd-MMM-yyyy"
                      showMonthDropdown
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      calendarClassName="custom-calendar"
                      onPaste={(event) => event.preventDefault()}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      disabled={listState.isLoading}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${
                        listState.showLoaderOnClick ? "search-btn" : ""
                      }`}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Record Count */}
                <div className="mt-2 mb-3 mx-1">
                  {certificateIssuanceReportData?.data?.totalRecords > 0 && (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing{" "}
                          {certificateIssuanceReportData?.data
                            ?.totalFilteredRecords || 0}{" "}
                          of{" "}
                          {certificateIssuanceReportData?.data?.totalRecords}{" "}
                          Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {certificateIssuanceReportData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Table States */}
                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation" />
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords || tableData.length === 0 ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info" />
                    </div>
                    <span>No certificate issuance records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData}
                      defaultPageSize={
                        certificateIssuanceReportData?.data
                          ?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE
                      }
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        (certificateIssuanceReportData?.data
                          ?.totalFilteredRecords || 0) /
                          (certificateIssuanceReportData?.data
                            ?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE)
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default CertificateIssuanceReport;
